import * as forge from 'node-forge';
import { PDFDocument, PDFDict, PDFName, PDFHexString, PDFString } from 'pdf-lib';

/**
 * Datos del certificado X.509 usado para firmar, extraídos directamente de la
 * estructura CMS/PKCS7 embebida en el PDF (campo /Contents del diccionario de
 * firma /Type /Sig). No depende del backend ni de metadata de negocio.
 */
export interface PdfCertificateInfo {
  subjectCN?: string;
  subjectFull?: string;
  issuerCN?: string;
  issuerFull?: string;
  serialNumber?: string;
  validFrom?: Date;
  validTo?: Date;
  signatureAlgorithm?: string;
  /**
   * Nombre del firmante extraído DIRECTAMENTE del diccionario de firma del
   * PDF (campo opcional /Name, ISO 32000) — NO del certificado. Si el
   * firmador no completó /Name, queda undefined (no se usa el CN del
   * certificado como respaldo, porque titular del certificado y firmante
   * declarado son conceptos distintos, aunque a menudo coincidan).
   */
  signerName?: string;
  /**
   * true si el emisor del certificado figura en la lista de Autoridades
   * Certificantes Licenciadas por la ONTI (Ente Licenciante, Ley 25.506).
   * Solo en ese caso corresponde llamarla "Firma Digital" en sentido legal;
   * caso contrario, aunque el certificado sea X.509 real, es "Firma
   * Electrónica" (no tiene la presunción legal de autoría/integridad).
   */
  isLicensedIssuer?: boolean;
  /** OIDs de la extensión certificatePolicies del certificado, si existe (diagnóstico). */
  policyOids?: string[];
}

/**
 * Nombres (o fragmentos) de las Autoridades Certificantes con licencia VIGENTE
 * otorgada por la ONTI / Ente Licenciante de la Infraestructura de Firma
 * Digital de la República Argentina (Ley 25.506), según
 * https://www.argentina.gob.ar/jefatura/innovacion-ciencia-y-tecnologia/innovacion/firma-digital/certificadores-licenciados
 * Cualquier certificado emitido por un emisor que NO figure aquí (p. ej.
 * "Autoridad Certificante CARDINAL") no es Firma Digital en sentido legal,
 * aunque tenga un certificado X.509 real y válido técnicamente.
 */
const LICENSED_ISSUER_PATTERNS = [
  'onti',
  'oficina nacional de tecnolog',
  'ac modernizaci',
  'secretar\u00eda de innovaci\u00f3n, ciencia y tecnolog',
  'ministerio de modernizaci',
  'encode s.a',
  'ac encode',
  'lakaut s.a',
  'ac lakaut',
  'box custodia de archivos',
  'ac box custodia',
  'digilogix s.a',
  'ac digilogix'
];

function isLicensedIssuer(issuerFull?: string, issuerCN?: string): boolean {
  const haystack = `${issuerFull || ''} ${issuerCN || ''}`.toLowerCase();
  return LICENSED_ISSUER_PATTERNS.some(p => haystack.includes(p));
}

/**
 * Parsea la estructura completa del PDF (xref, object streams comprimidos,
 * etc. — igual que hace Adobe Acrobat) usando pdf-lib, ubica cada diccionario
 * de firma (`/Type /Sig`) y devuelve los datos del certificado firmante de
 * cada uno, en el mismo orden en que aparecen los objetos en el documento.
 * Si un bloque no puede decodificarse se omite esa entrada sin interrumpir el resto.
 */
export async function extractPdfCertificates(pdfBytes: ArrayBuffer): Promise<PdfCertificateInfo[]> {
  const results: PdfCertificateInfo[] = [];

  let pdfDoc: PDFDocument;
  try {
    pdfDoc = await PDFDocument.load(pdfBytes, {
      ignoreEncryption: true,
      updateMetadata: false,
      throwOnInvalidObject: false
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[pdf-certificate-parser] No se pudo parsear la estructura completa del PDF con pdf-lib:', e);
    return results;
  }

  const contentsKey = PDFName.of('Contents');
  const byteRangeKey = PDFName.of('ByteRange');
  let sigDictsFound = 0;

  for (const [, obj] of pdfDoc.context.enumerateIndirectObjects()) {
    if (!(obj instanceof PDFDict)) {
      continue;
    }

    // El campo /Type /Sig es OPCIONAL según la especificación PDF (ISO 32000):
    // muchos firmadores no lo incluyen. La forma confiable de identificar un
    // diccionario de firma es que tenga a la vez /Contents (el CMS/PKCS7) y
    // /ByteRange (el rango de bytes firmado).
    if (!obj.has(contentsKey) || !obj.has(byteRangeKey)) {
      continue;
    }

    sigDictsFound++;

    // El nombre del firmante declarado en la PROPIA firma (no en el
    // certificado, no en datos de negocio): campo opcional /Name del
    // diccionario de firma del PDF.
    const decodeStr = (key: string): string | undefined => {
      try {
        const val = obj.lookupMaybe(PDFName.of(key), PDFString, PDFHexString);
        return (val as PDFString | PDFHexString | undefined)?.decodeText?.();
      } catch {
        return undefined;
      }
    };
    const dictSignerName = decodeStr('Name');

    try {
      const contents = obj.lookupMaybe(contentsKey, PDFHexString)
        ?? obj.lookupMaybe(contentsKey, PDFString, PDFHexString);
      if (!contents) {
        continue;
      }
      const bytes = (contents as PDFHexString | PDFString).asBytes();
      const der = bytesToBinaryString(bytes);
      const info = parseSignerCertificate(der);
      if (info) {
        // "Firmado por" sale ÚNICAMENTE del /Name del diccionario de firma
        // (dato declarado por la propia firma), nunca del certificado. Si el
        // firmador no completó /Name, queda undefined y el llamador usará
        // el dato de negocio disponible (no el CN del certificado).
        info.signerName = dictSignerName || undefined;
        results.push(info);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[pdf-certificate-parser] diccionario de firma (/Contents + /ByteRange) encontrado pero no es un CMS/PKCS7 válido:', e);
      continue;
    }
  }

  // eslint-disable-next-line no-console
  console.warn(`[pdf-certificate-parser] diccionarios de firma (/Contents+/ByteRange) encontrados: ${sigDictsFound}, certificados parseados: ${results.length}`);

  return results;
}

function bytesToBinaryString(bytes: Uint8Array): string {
  let out = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    out += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return out;
}

function parseSignerCertificate(derBinary: string): PdfCertificateInfo | null {
  // El campo /Contents de la firma se reserva con un tamaño fijo y se rellena
  // con bytes 0x00 al final (padding) para dejar espacio a la firma real.
  // Por eso el DER del CMS/PKCS7 casi siempre es MÁS CORTO que el string completo.
  // `parseAllBytes: false` evita que forge lance "Unparsed DER bytes remain..."
  // al encontrarse ese padding sobrante tras la estructura ASN.1 real.
  const asn1 = forge.asn1.fromDer(derBinary, { parseAllBytes: false } as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p7 = (forge.pkcs7 as any).messageFromAsn1(asn1);
  const certificates = p7.certificates;

  if (!certificates || certificates.length === 0) {
    return null;
  }

  // El certificado del firmante suele ser el que no es una CA (heurística
  // simple: si hay uno solo, ese es).
  const signerCert = certificates.length === 1
    ? certificates[0]
    : certificates.find((c: forge.pki.Certificate) => !isLikelyCA(c)) ?? certificates[0];

  const issuerCN = getAttribute(signerCert.issuer, 'CN');
  const issuerFull = formatDN(signerCert.issuer);
  const policyOids = getPolicyOids(signerCert);
  const licensedIssuer = isLicensedIssuer(issuerFull, issuerCN);

  return {
    subjectCN: getAttribute(signerCert.subject, 'CN'),
    subjectFull: formatDN(signerCert.subject),
    issuerCN,
    issuerFull,
    serialNumber: signerCert.serialNumber,
    validFrom: signerCert.validity?.notBefore,
    validTo: signerCert.validity?.notAfter,
    signatureAlgorithm: signerCert.siginfo?.algorithmOid,
    isLicensedIssuer: licensedIssuer,
    policyOids
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getPolicyOids(cert: any): string[] {
  try {
    const ext = cert.getExtension?.('certificatePolicies');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const policies = ext?.value ?? ext?.policies;
    if (!Array.isArray(policies)) {
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return policies.map((p: any) => p.id || p.policyIdentifier || String(p)).filter(Boolean);
  } catch {
    return [];
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isLikelyCA(cert: any): boolean {
  const basicConstraints = cert.getExtension?.('basicConstraints');
  return !!basicConstraints?.cA;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getAttribute(dn: any, shortName: string): string | undefined {
  const field = dn?.getField?.(shortName);
  return field?.value;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatDN(dn: any): string {
  if (!dn?.attributes) {
    return '';
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return dn.attributes
    .map((a: any) => `${a.shortName || a.name}=${a.value}`)
    .join(', ');
}
